import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, Box, Divider
} from "@mui/material";
import { useTranslation } from "react-i18next";
import supabase from "../../../../utils/supabase";

const LANG_OPTIONS = ["ja", "en", "zh", "es", "fr", "ru"];

export const parseContentToMap = (content) => {
  if (!content) return {};
  if (typeof content === "object") return content;
  try {
    const parsed = JSON.parse(content);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return { ja: content };
  }
};

export const sanitizeHtml = (html = "") => {
  const fallback = (s = "") =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return fallback(html);
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(html || "", "text/html");
  const allowedTags = new Set(["b", "strong", "i", "em", "u", "p", "br", "ul", "ol", "li", "blockquote", "a"]);
  const walk = (node) => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === 1) {
        const tag = child.tagName.toLowerCase();
        if (!allowedTags.has(tag)) {
          child.replaceWith(...Array.from(child.childNodes));
          continue;
        }
        Array.from(child.attributes).forEach((attr) => {
          const name = attr.name.toLowerCase();
          if (tag === "a" && name === "href") {
            const href = child.getAttribute("href") || "";
            if (!/^https?:\/\//i.test(href)) {
              child.removeAttribute("href");
            } else {
              child.setAttribute("rel", "noreferrer");
              child.setAttribute("target", "_blank");
            }
          } else {
            child.removeAttribute(attr.name);
          }
        });
        walk(child);
      }
    }
  };
  walk(doc.body);
  return doc.body.innerHTML;
};

function CommentEditForm({ initialContent, onSave, onCancel, t, initialLang }) {
  const [contentMap, setContentMap] = useState(() => parseContentToMap(initialContent));
  const initialOrder = Object.keys(contentMap).length
    ? Object.keys(contentMap)
    : [initialLang || "ja"];
  const [langOrder, setLangOrder] = useState(initialOrder);
  const editorRefs = useRef({});

  useEffect(() => {
    langOrder.forEach((code) => {
      const el = editorRefs.current[code];
      if (el) {
        const nextContent = sanitizeHtml(contentMap[code] || "");
        if (el.innerHTML !== nextContent) {
          el.innerHTML = nextContent;
        }
      }
    });
  }, [langOrder]); // run when language set changes to sync initial HTML

  const handleContentUpdate = (code, html) => {
    setContentMap((prev) => ({ ...prev, [code]: sanitizeHtml(html) }));
  };

  const handleSaveClick = () => {
    const finalContentMap = { ...contentMap };
    Object.keys(finalContentMap).forEach((k) => {
      if (!finalContentMap[k] || finalContentMap[k] === "<p><br></p>") {
        delete finalContentMap[k];
      }
    });

    if (Object.keys(finalContentMap).length === 0) {
      onCancel();
      return;
    }
    const payloadContent = JSON.stringify(finalContentMap);
    onSave(payloadContent);
  };

  const applyCommand = (code, command, value = null) => {
    const el = editorRefs.current[code];
    if (!el) return;
    el.focus();
    document.execCommand(command, false, value);
    const html = el.innerHTML;
    handleContentUpdate(code, html);
  };

  const setLink = (code) => {
    const url = window.prompt("Link URL (https://...)");
    if (url) {
      applyCommand(code, "createLink", url);
    }
  };

  const handleRemoveLanguage = (code) => {
    if (langOrder.length <= 1) return;
    if (window.confirm(t("removeLangConfirm", `Are you sure you want to remove the ${code.toUpperCase()} version?`))) {
      setContentMap((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      setLangOrder((prev) => prev.filter((c) => c !== code));
    }
  };

  const handleAddLanguage = (code) => {
    if (Object.prototype.hasOwnProperty.call(contentMap, code)) return;
    setContentMap((prev) => ({ ...prev, [code]: "" }));
    setLangOrder((prev) => [...prev, code]);
  };

  const availableLangsToAdd = LANG_OPTIONS.filter((code) => !Object.prototype.hasOwnProperty.call(contentMap, code));
  return (
    <Stack spacing={2}>
      <Stack spacing={3}>
        {langOrder.map((code) => (
          <Box key={code} sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{code.toUpperCase()}</Typography>
              {langOrder.length > 1 && (
                <Button size="small" color="error" onClick={() => handleRemoveLanguage(code)}>
                  {t("removeLangVersion", "Remove {{lang}} version", { lang: code.toUpperCase() })}
                </Button>
              )}
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-start" flexWrap="wrap" sx={{ mb: 1 }}>
              <Button size="small" variant="outlined" onClick={() => applyCommand(code, "bold")}>
                {t("toolbar.bold", "Bold")}
              </Button>
              <Button size="small" variant="outlined" onClick={() => applyCommand(code, "italic")}>
                {t("toolbar.italic", "Italic")}
              </Button>
              <Button size="small" variant="outlined" onClick={() => applyCommand(code, "underline")}>
                {t("toolbar.underline", "Underline")}
              </Button>
              <Button size="small" variant="outlined" onClick={() => applyCommand(code, "insertUnorderedList")}>
                {t("toolbar.list", "• List")}
              </Button>
              <Button size="small" variant="outlined" onClick={() => applyCommand(code, "formatBlock", "blockquote")}>
                {t("toolbar.quote", "Quote")}
              </Button>
              <Button size="small" variant="outlined" onClick={() => setLink(code)}>
                {t("toolbar.link", "Link")}
              </Button>
            </Stack>
            <Box
              ref={(el) => {
                if (el) editorRefs.current[code] = el;
              }}
              contentEditable
              suppressContentEditableWarning
              onInput={(e) => handleContentUpdate(code, e.currentTarget.innerHTML)}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1,
                minHeight: 200,
                backgroundColor: (theme) => theme.palette.background.paper,
                color: (theme) => theme.palette.text.primary,
                "& a": { color: "inherit" },
                "&:focus": { outline: "2px solid", outlineColor: "primary.main" },
              }}
              aria-label={t("editLabel", "Edit Comment")}
            />
          </Box>
        ))}
      </Stack>

      {availableLangsToAdd.length > 0 && (
        <Box>
          <Typography variant="body2" gutterBottom>
            {t("addAnotherLanguage", "Add another language")}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {availableLangsToAdd.map((code) => (
              <Button
                key={code}
                size="small"
                variant="outlined"
                onClick={() => handleAddLanguage(code)}
              >
                + {code.toUpperCase()}
              </Button>
            ))}
          </Stack>
        </Box>
      )}

      <DialogActions sx={{ px: 0, pt: 2 }}>
        <Button onClick={onCancel}>{t("common:cancel", "Cancel")}</Button>
        <Button variant="contained" onClick={handleSaveClick}>
          {t("common:saveChanges", "Save Changes")}
        </Button>
      </DialogActions>
    </Stack>
  );
}

export default function DialogContributorCommentEditor({ open, mode = "add", comment, user, onClose }) {
  const { t, i18n, ready } = useTranslation(["comments", "common"], { useSuspense: false });

  if (!ready) return null;

  const initialContent = mode === "edit" ? comment?.content : JSON.stringify({ [i18n.language]: "" });
  const originalMap = mode === "edit" ? parseContentToMap(comment?.content) : {};

  const handleSave = async (content) => {
    if (!user) {
      onClose?.(false);
      return;
    }
    try {
      if (mode === "edit" && comment?.id) {
        await supabase.from("recent_comments").update({ content, updated_at: new Date() }).eq("id", comment.id);
      } else {
        await supabase.from("recent_comments").insert({
          user_id: user.id,
          username: user.user_metadata?.display_name || user.email,
          content,
        });
      }
      onClose?.(true);
    } catch (e) {
      console.error("Failed to save comment", e);
      onClose?.(false);
    } finally {
      // no-op
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose?.(false)} fullWidth maxWidth="md">
      <DialogTitle>
        {mode === "edit"
          ? t("editLabel", "Edit Comment")
          : t("addComment", "Add Comment")}
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "80vh" }}>
        <Stack spacing={2}>
          <CommentEditForm
            initialContent={initialContent}
            onSave={handleSave}
            onCancel={() => onClose?.(false)}
            t={t}
            initialLang={i18n.language}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
